import { useEffect, useRef, useState } from 'react';
import { holeTurnText, type HoleTurn } from '../lib/holeTurn';

/**
 * The golf maneuver banner: shown as the golfer leaves one hole for the next.
 *
 * Deliberately shaped like a street-nav maneuver card rather than a toast. A
 * golfer glancing at a buggy screen in sunlight reads shape and position before
 * they read words, and every driver already knows what a card at the top of a
 * map means. Reusing that costs nothing and teaches nothing new.
 *
 * It dismisses itself. Nothing on a moving buggy should require a tap to clear:
 * the tap target is small, the screen is bouncing, and a banner that waits for
 * acknowledgement ends up parked over the hole map for the rest of the round.
 * Tapping it early still works for anyone who wants it gone.
 */

/** Long enough to read at a glance and look up again; short enough to not sit. */
const SHOW_MS = 9000;

export default function HoleTurnBanner({
  turn,
  onDone,
}: {
  turn: HoleTurn | null;
  onDone: () => void;
}) {
  const [leaving, setLeaving] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    // Clear on every change: two hole changes in quick succession (a detector
    // settling, or a golfer flicking through holes) would otherwise leave the
    // first banner's timer alive to dismiss the second one early.
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (!turn) return;

    setLeaving(false);
    timers.current.push(
      window.setTimeout(() => setLeaving(true), SHOW_MS - 400),
      window.setTimeout(onDone, SHOW_MS)
    );
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [turn, onDone]);

  if (!turn) return null;
  const { lead, detail } = holeTurnText(turn);

  return (
    <button
      className={`hole-turn ${leaving ? 'leaving' : ''}`}
      onClick={onDone}
      aria-live="polite"
    >
      <span className="ht-arrow" aria-hidden>
        {turn.direction === 'back' ? (
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M11 5l-7 7 7 7" /><path d="M4 12h16" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M13 5l7 7-7 7" /><path d="M20 12H4" />
          </svg>
        )}
      </span>
      <span className="ht-copy">
        <span className="ht-lead">{lead}</span>
        <span className="ht-detail">{detail}</span>
      </span>
    </button>
  );
}
